import React, { useState, useEffect, useRef } from 'react'
import { User } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getHardcodedAvatar, hasHardcodedAvatar } from '@/data/avatars'

interface AvatarProps {
  src?: string | null
  alt?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  fallbackIcon?: React.ReactNode
  priority?: boolean // 是否优先加载
  lazy?: boolean // 是否懒加载
  userId?: string // 新增：用于获取硬编码头像
}

const sizeClasses = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm', 
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg'
}

const iconSizes = {
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32
}

export function Avatar({
  src,
  alt = 'Avatar',
  size = 'md',
  className,
  fallbackIcon,
  priority = false,
  lazy = true,
  userId
}: AvatarProps) {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [shouldLoad, setShouldLoad] = useState(!lazy || priority)
  const [hardcodedAvatar, setHardcodedAvatar] = useState<string | null>(null)
  const [hardcodedLoading, setHardcodedLoading] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // 异步加载硬编码头像（仅当没有用户上传的头像时）
  useEffect(() => {
    // 如果有用户上传的头像，优先使用用户头像，不加载硬编码头像
    if (!userId || src || !hasHardcodedAvatar(userId)) return

    setHardcodedLoading(true)
    getHardcodedAvatar(userId)
      .then((avatar) => {
        setHardcodedAvatar(avatar)
        if (avatar) {
          setImageLoaded(true)
          setImageError(false)
        }
      })
      .catch((error) => {
        console.warn('加载硬编码头像失败:', error)
        setHardcodedAvatar(null)
      })
      .finally(() => {
        setHardcodedLoading(false)
      })
  }, [userId, src])

  // 优先级：用户上传的头像 > 硬编码头像
  const finalSrc = src || hardcodedAvatar

  // 调试信息
  useEffect(() => {
    if (userId && process.env.NODE_ENV === 'development') {
      console.log(`Avatar for ${userId}:`, {
        userUploaded: !!src,
        hasHardcoded: hasHardcodedAvatar(userId),
        hardcodedLoaded: !!hardcodedAvatar,
        finalSrc: finalSrc ? 'available' : 'none'
      })
    }
  }, [userId, src, hardcodedAvatar, finalSrc])

  // 懒加载逻辑
  useEffect(() => {
    if (!lazy || priority || shouldLoad) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true)
            observer.disconnect()
          }
        })
      },
      {
        rootMargin: '50px' // 提前50px开始加载
      }
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [lazy, priority, shouldLoad])

  // 图片加载处理
  const handleImageLoad = () => {
    setImageLoaded(true)
    setImageError(false)
  }

  const handleImageError = () => {
    setImageError(true)
    setImageLoaded(false)
  }

  // 预加载图片（仅用于用户上传的头像）
  useEffect(() => {
    if (!shouldLoad || !finalSrc || imageLoaded || imageError) return

    // 如果是硬编码头像，已经在上面的 useEffect 中处理了
    if (hardcodedAvatar) {
      return
    }

    // 只有用户上传的头像才需要网络请求预加载
    if (src && src === finalSrc) {
      const img = new Image()
      img.onload = handleImageLoad
      img.onerror = handleImageError
      img.src = finalSrc
    }
  }, [shouldLoad, finalSrc, imageLoaded, imageError, hardcodedAvatar, src])

  const showFallback = !finalSrc || imageError || !imageLoaded

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative rounded-full bg-gray-100 flex items-center justify-center overflow-hidden transition-all duration-300',
        sizeClasses[size],
        className
      )}
    >
      {/* 加载状态 */}
      {(shouldLoad && finalSrc && !imageLoaded && !imageError) || hardcodedLoading ? (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-full" />
      ) : null}

      {/* 头像图片 */}
      {shouldLoad && finalSrc && imageLoaded && !imageError && (
        <img
          ref={imgRef}
          src={finalSrc}
          alt={alt}
          className={cn(
            'w-full h-full object-cover transition-opacity duration-300',
            imageLoaded ? 'opacity-100' : 'opacity-0'
          )}
          loading={priority ? 'eager' : 'lazy'}
        />
      )}

      {/* 后备图标 */}
      {showFallback && (
        <div className={cn(
          'flex items-center justify-center text-gray-400 transition-opacity duration-300',
          shouldLoad && src && !imageLoaded && !imageError ? 'opacity-0' : 'opacity-100'
        )}>
          {fallbackIcon || <User size={iconSizes[size]} />}
        </div>
      )}

      {/* 加载失败提示 */}
      {imageError && (
        <div className="absolute inset-0 bg-red-50 flex items-center justify-center">
          <div className="text-red-400 text-xs">!</div>
        </div>
      )}
    </div>
  )
}

// 专门用于用户头像的组件
interface UserAvatarProps extends Omit<AvatarProps, 'fallbackIcon'> {
  username?: string
  userId?: string // 新增：用于获取硬编码头像
}

export function UserAvatar({ username, userId, alt, ...props }: UserAvatarProps) {
  // 使用用户名首字母作为后备显示
  const fallbackText = username ? username.charAt(0).toUpperCase() : '?'

  return (
    <Avatar
      {...props}
      userId={userId}
      alt={alt || `${username}的头像`}
      fallbackIcon={
        <span className="font-medium text-gray-600">
          {fallbackText}
        </span>
      }
    />
  )
}

// 头像骨架屏组件
export function AvatarSkeleton({ size = 'md', className }: Pick<AvatarProps, 'size' | 'className'>) {
  return (
    <div
      className={cn(
        'rounded-full bg-gray-200 animate-pulse',
        sizeClasses[size],
        className
      )}
    />
  )
}
